import { computed, ref } from 'vue';
import { useHCommon, useOCommon, useOSettings, useOUserSettingsStore } from '@onify/helix-core';

const PROCESS_KEY = 'set-my-contact-information-preference';
const POLL_INTERVAL_MS = 500;
const MAX_POLL_ATTEMPTS = 60;
const VALID_STATUSES = new Set(['opted_in', 'opted_out']);

export function normalizeContactDetailsPreference(value) {
  const status = value?.status;
  return VALID_STATUSES.has(status) ? status : 'unknown';
}

export function useContactDetailsPreference() {
  const { hHttpRequest } = useHCommon();
  const { oUser } = useOCommon();
  const { citizen_my_profile } = useOSettings();
  const { oRefetchUserSettings } = useOUserSettingsStore();
  const saving = ref(false);

  const config = computed(() => citizen_my_profile?.value?.contact_details_preference || {});
  const featureEnabled = computed(() => citizen_my_profile?.value?.enabled === true && config.value.enabled === true);
  const status = computed(() =>
    normalizeContactDetailsPreference(oUser.value?.custom?.citizen?.profile_information?.contact_details_preference),
  );
  const isOptedIn = computed(() => status.value === 'opted_in');
  const isOptedOut = computed(() => status.value === 'opted_out');
  const confirmOptOut = computed(() => config.value.confirm_opt_out !== false);
  const contactFieldsDisabled = computed(
    () => featureEnabled.value && isOptedOut.value && config.value.disable_contact_fields_when_opted_out !== false,
  );

  const getProcess = async (processId) => {
    const { execute } = hHttpRequest({
      url: `my/processes/${processId}`,
      immediate: false,
      method: 'get',
    });
    const response = await execute();
    if (!response?.ok) throw new Error('Failed to check contact details preference process');
    return response.json();
  };

  const waitForCompletion = async (processId) => {
    for (let attempt = 0; attempt < MAX_POLL_ATTEMPTS; attempt += 1) {
      const process = await getProcess(processId);
      const statusName = process?.status?.name;

      if (statusName === 'Completed') return process;
      if (statusName === 'Stopped') {
        throw new Error(process?.status?.statusmessage || 'Contact details preference process stopped');
      }
      if (statusName !== 'Running') {
        throw new Error(`Unknown contact details preference process status: ${process?.status?.statuskey || 'unknown'}`);
      }

      await new Promise((resolve) => setTimeout(resolve, POLL_INTERVAL_MS));
    }

    throw new Error('Contact details preference process timed out');
  };

  const setPreference = async (nextStatus) => {
    if (!VALID_STATUSES.has(nextStatus)) throw new Error('Invalid contact details preference status');
    if (!featureEnabled.value || saving.value) return false;

    saving.value = true;
    try {
      const { execute } = hHttpRequest({
        url: `my/processes/start/${PROCESS_KEY}`,
        immediate: false,
        method: 'post',
        payload: {
          title: 'Set contact details preference',
          description: 'Set contact details preference',
          input: { status: nextStatus },
        },
      });
      const response = await execute();
      if (!response?.ok) throw new Error('Failed to start contact details preference process');

      const process = await response.json();
      if (!process?.id) throw new Error('Contact details preference process id is missing');

      await waitForCompletion(process.id);
      await oRefetchUserSettings();
      return true;
    } finally {
      saving.value = false;
    }
  };

  return {
    featureEnabled,
    status,
    isOptedIn,
    isOptedOut,
    confirmOptOut,
    contactFieldsDisabled,
    saving,
    setPreference,
  };
}
